import ExcelJS from "exceljs";
import {
  generateWeeksForPhases,
  buildEntryMap,
  buildCellKey,
  formatDateSwiss,
  sumCellEntries,
  STATUS_CONFIG,
} from "./zahlungsplanUtils";
import { calculateMwstAmounts } from "../../services/mwstService";

const STATUS_FILLS = {
  expected: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5E7EB" } },
  expecting: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5E7EB" } },
  sent: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF08A" } },
  received: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF08A" } },
  entered_payment: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF08A" } },
  paid: { type: "pattern", pattern: "solid", fgColor: { argb: "FFBBF7D0" } },
  overdue: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFECACA" } },
};

export const exportZahlungsplanToExcel = async ({
  projectCode,
  projectTitle,
  phases,
  entries,
  columns,
  globalSettings,
  hideEmptyWeeks = false,
  selectedPlaner = null,
  incomeBudgets = {},
}) => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Zahlungsplan");

  // entries mitgeben (wie im Raster): sonst fehlen Einträge ausserhalb des
  // Phasenzeitraums als Zeile, stecken aber im Fusszeilen-Total.
  const allWeeks = generateWeeksForPhases(phases, 8, entries);
  const entryMap = buildEntryMap(entries);

  // Filter weeks if hideEmptyWeeks is active
  const weeks = hideEmptyWeeks
    ? allWeeks.filter((week) => {
        if (week.isCurrentWeek) return true;
        const weekKey = `${week.year}-${week.number}`;
        return Object.keys(entryMap).some((key) => key.endsWith(`|${weekKey}`));
      })
    : allWeeks;

  // Column layout: Rechnungsnr. | KW | Datum | ...data columns...
  const dataColOffset = 4; // data columns start at column 4

  // -- Row 1: Title --
  ws.getCell("A1").value = `Zahlungsplan – ${projectCode}${projectTitle ? ` – ${projectTitle}` : ""}`;
  ws.getCell("A1").font = { bold: true, size: 13 };
  ws.mergeCells(1, 1, 1, 3 + columns.length);

  // -- Row 2: Settings --
  const nkInfo = globalSettings.nebenkostenSatz ? ` | Nebenkosten: ${globalSettings.nebenkostenSatz}%` : "";
  ws.getCell("A2").value = `MwSt: ${globalSettings.mwstSatz}%${nkInfo} | Zahlungsfrist: ${globalSettings.zahlungsfrist} Tage | Exportiert: ${formatDateSwiss(new Date())}`;
  ws.getCell("A2").font = { size: 9, italic: true, color: { argb: "FF666666" } };
  ws.mergeCells(2, 1, 2, 3 + columns.length);

  // -- Row 4: Column headers --
  const headerRow = 4;

  ws.getCell(headerRow, 1).value = "Rechnungsnr.";
  ws.getCell(headerRow, 1).font = { bold: true, size: 9 };
  ws.getColumn(1).width = 16;

  ws.getCell(headerRow, 2).value = "KW";
  ws.getCell(headerRow, 2).font = { bold: true, size: 9 };
  ws.getColumn(2).width = 8;

  ws.getCell(headerRow, 3).value = "Datum";
  ws.getCell(headerRow, 3).font = { bold: true, size: 9 };
  ws.getColumn(3).width = 12;

  columns.forEach((col, idx) => {
    const colNum = idx + dataColOffset;
    ws.getCell(headerRow, colNum).value = col.label;
    ws.getCell(headerRow, colNum).font = { bold: true, size: 8 };
    ws.getCell(headerRow, colNum).alignment = { horizontal: "center", wrapText: true };
    ws.getColumn(colNum).width = 15;

    // Light color for column type
    if (col.type === "einnahmen") {
      ws.getCell(headerRow, colNum).fill = {
        type: "pattern", pattern: "solid",
        fgColor: { argb: "FFE0EDFF" },
      };
    } else if (col.type === "ausgaben") {
      ws.getCell(headerRow, colNum).fill = {
        type: "pattern", pattern: "solid",
        fgColor: { argb: "FFFEE2E2" },
      };
    }
  });

  // Header row border
  const totalCols = 3 + columns.length;
  for (let c = 1; c <= totalCols; c++) {
    ws.getCell(headerRow, c).border = {
      bottom: { style: "medium", color: { argb: "FF333333" } },
    };
  }

  // -- Data rows --
  let row = headerRow + 1;
  weeks.forEach((week) => {
    const weekKey = `${week.year}-${week.number}`;
    const isCurrentWeek = week.isCurrentWeek;

    // Collect invoice references for this week
    const invoiceRefs = [];

    ws.getCell(row, 2).value = `KW ${week.number}`;
    ws.getCell(row, 2).font = { size: 8, bold: isCurrentWeek };
    ws.getCell(row, 3).value = formatDateSwiss(week.startDate);
    ws.getCell(row, 3).font = { size: 8 };

    if (isCurrentWeek) {
      for (let c = 1; c <= totalCols; c++) {
        ws.getCell(row, c).fill = {
          type: "pattern", pattern: "solid",
          fgColor: { argb: "FFEEF2FF" },
        };
      }
    }

    columns.forEach((col, idx) => {
      const colNum = idx + dataColOffset;
      // Name- UND ID-Schlüssel probieren (wie das Raster): alte Einträge
      // speichern die Phasen-ID — sonst fehlen sie als Zeile, zählen im Total.
      const phaseName = col.phase?.name || "";
      const phaseId = col.phase?.id || "";

      let cellEntries = [];
      if (col.type === "einnahmen") {
        cellEntries = [
          ...(entryMap[buildCellKey("Einnahme", phaseName, weekKey)] || []),
          ...(phaseId !== phaseName
            ? entryMap[buildCellKey("Einnahme", phaseId, weekKey)] || []
            : []),
        ];
      } else if (col.type === "ausgaben") {
        cellEntries = [
          ...(entryMap[buildCellKey("Ausgabe", phaseName, weekKey, col.partnerId)] || []),
          ...(phaseId !== phaseName
            ? entryMap[buildCellKey("Ausgabe", phaseId, weekKey, col.partnerId)] || []
            : []),
        ];
      }

      if (cellEntries.length > 0) {
        const cell = ws.getCell(row, colNum);
        // Summe aller Einträge der Zelle (wie im Raster), damit die Zeilen
        // zum Fusszeilen-Total aufsummieren.
        cell.value = sumCellEntries(cellEntries);
        cell.numFmt = "#'##0.00";
        cell.font = { size: 8 };
        cell.alignment = { horizontal: "right" };

        // Status fill (erster Eintrag)
        const statusFill = STATUS_FILLS[cellEntries[0].status];
        if (statusFill && !isCurrentWeek) {
          cell.fill = statusFill;
        }

        // Collect invoice references (alle Einträge)
        cellEntries.forEach((entry) => {
          if (entry.invoice_reference) {
            invoiceRefs.push(entry.invoice_reference);
          }
        });
      }
    });

    // Write invoice references in the first column
    if (invoiceRefs.length > 0) {
      const uniqueRefs = [...new Set(invoiceRefs)];
      ws.getCell(row, 1).value = uniqueRefs.join(", ");
      ws.getCell(row, 1).font = { size: 7, color: { argb: "FF666666" } };
    }

    row++;
  });

  // -- Footer rows --
  const nebenkostenSatz = globalSettings.nebenkostenSatz || 0;
  const footerStart = row + 1;
  const footerBorder = {
    top: { style: "medium", color: { argb: "FF333333" } },
  };

  // Pre-calculate per-column totals
  const colTotals = columns.map((col) => {
    let colEntries;
    if (col.type === "einnahmen") {
      colEntries = entries.filter(
        (e) => e.typ === "Einnahme" && (e.phase === col.phase.name || e.phase === col.phase.id)
      );
    } else if (col.type === "ausgaben") {
      // String()-Vergleich wie im Footer (numerische Alt-partner_id)
      colEntries = entries.filter(
        (e) =>
          e.typ === "Ausgabe" &&
          (e.phase === col.phase.name || e.phase === col.phase.id) &&
          ((e.partner_id && String(e.partner_id) === String(col.partnerId)) ||
            (!e.partner_id && col.partnerId === "other"))
      );
    } else {
      colEntries = [];
    }

    const totalNetto = colEntries.reduce((s, e) => s + (e.netto_betrag || e.betrag || 0), 0);
    const nebenkosten = col.type === "einnahmen" && nebenkostenSatz
      ? totalNetto * (nebenkostenSatz / 100)
      : 0;
    const subtotal = totalNetto + nebenkosten;
    const mwst = calculateMwstAmounts(subtotal, globalSettings.mwstSatz, false);

    const phaseKey = col.phaseKey;
    const budget = col.type === "einnahmen"
      ? (incomeBudgets[phaseKey] != null ? incomeBudgets[phaseKey] : (parseFloat(col.phase.budget) || 0))
      : 0;
    const diff = col.type === "einnahmen" ? budget - subtotal : 0;

    return { totalNetto, subtotal, mwstBetrag: mwst.mwstBetrag, bruttoBetrag: mwst.bruttoBetrag, budget, diff };
  });

  // Build footer row definitions
  const footerRows = [
    { label: "Total Netto", field: "totalNetto", bold: false },
    ...(nebenkostenSatz > 0
      ? [{ label: `Total inkl. NK (${nebenkostenSatz}%)`, field: "subtotal", bold: false, onlyEinnahmen: true }]
      : []),
    { label: `MwSt (${globalSettings.mwstSatz}%)`, field: "mwstBetrag", bold: false },
    { label: "Total Brutto", field: "bruttoBetrag", bold: true },
    { label: "Budget", field: "budget", bold: false, onlyEinnahmen: true },
    { label: "Differenz", field: "diff", bold: true, onlyEinnahmen: true, colored: true },
  ];

  footerRows.forEach((fDef, fIdx) => {
    const fRow = footerStart + fIdx;
    ws.getCell(fRow, 1).value = fDef.label;
    ws.getCell(fRow, 1).font = { bold: true, size: 9 };
    if (fIdx === 0) ws.getCell(fRow, 1).border = footerBorder;

    columns.forEach((col, cIdx) => {
      const colNum = cIdx + dataColOffset;
      if (fDef.onlyEinnahmen && col.type !== "einnahmen") return;

      const value = colTotals[cIdx][fDef.field] || 0;
      if (value === 0) return;

      const cell = ws.getCell(fRow, colNum);
      cell.value = value;
      cell.numFmt = "#'##0.00;-#'##0.00";
      cell.alignment = { horizontal: "right" };

      if (fDef.colored) {
        cell.font = {
          bold: true,
          size: 9,
          color: value < 0 ? { argb: "FFDC2626" } : { argb: "FF059669" },
        };
      } else {
        cell.font = { bold: fDef.bold, size: 9 };
      }

      if (fIdx === 0) cell.border = footerBorder;
    });
  });

  // -- Download --
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const now = new Date();
  const dateStr = String(now.getFullYear()).slice(2)
    + String(now.getMonth() + 1).padStart(2, "0")
    + String(now.getDate()).padStart(2, "0");
  const suffix = selectedPlaner ? `-${selectedPlaner.name}` : "";
  a.download = `${projectCode}_TAB_${dateStr}_Zahlungsplan${suffix}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
};
